import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Accordion from "../../../components/shared/Accordion";
import EmailThread from "../../../components/shared/EmailThread";
import AiMessageAssistPopover from "../../../components/shared/AiMessageAssistPopover";
import { fetchEmailThread, replyToEmailThread, registerWatchedThread, updateSavedAirlineRate, getListOfTemplates, emailTemplatePopulated, getAllAirlines, addNewAirline, saveDraftEmail, sendEmail, parseAirlineRateFromInput, postQuoteMessageAssist } from "../../../Api";
import { shallowEqual, useSelector } from "react-redux";
import { FinalRateSection, recordHasRates } from "./TransportConversation";
import { useParams } from "react-router-dom";
import { formatMissingFieldLabel } from "../../../Helper/FilterationFunctions";
import { formatTemplateOptionLabel } from "../../../Helper/emailDisplayUtils";
import { showSwal } from "../../../Helper/ShowAlert";
import { EmailThreadSkeleton } from "../../../components/shared/SkeletonComponents";
import TemplateSwitchConfirmModal from "../../../components/shared/TemplateSwitchConfirmModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleNotch, faRotateRight } from "@fortawesome/free-solid-svg-icons";
import toast from "react-hot-toast";
import { stripHtmlToText } from "../../../Helper/aiEmailParseHelpers";
import AnimatedLoader from "../../../motionKit/AnimatedLoader";
import { sortThreadMessages } from "./threadOrdering";

// Helper function to parse email addresses from headers
const parseEmailAddresses = (headerValue) => {
  if (!headerValue) return [];
  return headerValue
    .split(',')
    .map(addr => {
      const trimmed = addr.trim();
      if (trimmed.includes("<")) {
        return trimmed.split("<")[1].replace(">", "").trim();
      }
      return trimmed;
    })
    .filter(addr => addr && addr.length > 0);
};

const parseEmailMessage = (message) => {
  const fromHeader = message.headers.from || "";
  const toHeader = message.headers.to || "";
  const ccHeader = message.headers.cc || "";
  const bccHeader = message.headers.bcc || "";

  // Keep the raw ms epoch alongside the display string. combinedAirlineMessages
  // sorts by this, and without it the comparator falls through to parsing the
  // localized `date` string (locale-dependent → NaN in some builds), which
  // makes new SSE messages land in the wrong position.
  const internalMs = parseInt(message.internalDate, 10);

  return {
    sender: fromHeader,
    email: fromHeader.includes("<")
      ? fromHeader.split("<")[1].replace(">", "")
      : fromHeader,
    to: parseEmailAddresses(toHeader),
    cc: parseEmailAddresses(ccHeader),
    bcc: parseEmailAddresses(bccHeader),
    subject: message.headers.subject,
    date: new Date(internalMs).toLocaleString(),
    // null, not 0: 0 is finite, so it passed the old sort guard and pinned the
    // message to epoch 1970 — the top of the thread. See threadOrdering.js.
    internalDate: Number.isFinite(internalMs) ? internalMs : null,
    html: message.body,
    messageId: message.headers.messageId,
    id: message.id,
    attachments: message.attachments || [],
  };
};

export function renderCostBreakdown(data, forType = "airline") {
  if (!data) return "";
  const airlineTotal = (data.FR + data.WR + data.HF) * data.CW * (1 + data.FS / 100) + (data?.isFSc ? data?.FSc : 0) * data?.CW;
  const airlineTotalRate = airlineTotal / data.CW

  const HaulerCost = data.TC + (data?.isTSc ? data.TSc : 0)
  const HaulerRate = HaulerCost / data.CW
  return `
    <strong>Cost Breakdown:</strong><br />
    • Screening By: ${data?.isFSc ? "Airline" : data?.isTSc ? "Transporter" : "Not decided"}<br />
    ${forType == "airline" ? `
    • CW (Chargeable Weight): ${data?.CW || 0}<br />
    • FR (Freight Rate): £${(data?.FR || 0).toFixed(2)} /kg<br />
    • WR (War Risk Surcharge): £${(data?.WR || 0).toFixed(2)} /kg<br />
    • HF (Handling Fees): £${(data?.HF || 0).toFixed(2)} /kg<br />
    • FS (Fuel Surcharge): ${(data?.FS || 0).toFixed(2)} %<br />
    • PFSc (Primary Flight Screening): £${(data?.PFSc || 0).toFixed(2)} /kg<br />
    • SFSc (Secondary Flight Screening): £${(data?.SFSc || 0).toFixed(2)} /kg<br />
    • FFSc (Final Flight Screening): £${(data?.isFSc ? data?.FSc : 0).toFixed(2)}<br />
    <strong>Final Rate:</strong> £${(parseFloat(airlineTotalRate) || 0).toFixed(2)} /kg<br />
    <strong>Total Amount To be Paid on booking from our end:</strong> £${(airlineTotal || 0).toFixed(2)}<br /><br />
    ` : `
    • CW (Chargeable Weight): ${data?.CW || 0}<br />
    • TC (Calculated Transport Cost): ${data?.TC || 0}<br />
    • PTSc (Primary Transport Screening): £${(data?.PTSc || 0).toFixed(2)}<br />
    • STSc (Secondary Transport Screening): £${(data?.STSc || 0).toFixed(2)}<br />
    • FTSc (Final Transport Screening): £${(data?.isTSc ? data?.TSc : 0).toFixed(2)}<br />
    • Org (Origin): ${data?.from}<br />
    • DA (Destination Airport): ${data?.destinationAirport}<br />
    <strong>Final Rate:</strong> £${(parseFloat(HaulerRate) || 0).toFixed(2)}<br />
    <strong>Total Amount To be Paid on booking from our end:</strong> £${(HaulerCost).toFixed(2)}<br /><br />`}
      If any of the applied components 
  <em>(${forType == "airline" ? `FR, WR, HF, FS, PFSc, SFSc` : `TC, PTSc, STSc`})</em> require adjustments 
  based on the shipment characteristics 
  (e.g., hazardous cargo, stackable restrictions, special handling), 
  please highlight the necessary corrections.<br /><br />
  `;
}

export const highlightedPayable = (data, forType = "airline", simple = true) => {
  if (!data) return "";
  const airlineTotal = (data.FR + data.WR + data.HF) * data.CW * (1 + data.FS / 100) + (data?.isFSc ? data?.FSc : 0) * data?.CW;
  const HaulerCost = data.TC + (data?.isTSc ? data.TSc : 0)
  return simple ? `<span style="font-size:15px; color:#374151; font-weight:600;">Total Payable Rate:</span>
    &nbsp;<span style="font-size:22px; font-weight:bold; color:#047857;">${((forType == "airline" ? airlineTotal : HaulerCost) / data?.CW).toFixed(2)} (£/kg)</span><br />` : `<div style="background:#f9fafb; border:1px solid #d1d5db; border-radius:6px; padding:14px 18px; margin-bottom:20px; text-align:center;">
    <span style="font-size:15px; color:#374151; font-weight:600;">Total Payable Rate:</span><br />
    <span style="font-size:22px; font-weight:bold; color:#047857;">${((forType == "airline" ? airlineTotal : HaulerCost) / data?.CW).toFixed(2)} (£/kg)</span>
  </div>`
}

export default function AirlineConversation({
  my_saved_airlines = [],
  setPanelTriggerKey,
  disabled,
  USER_EMAIL,
  defaultOpen,
  highlightThreadId,
  openPanel,
  setRefetchTrigger,
  setFqctTrigger = setRefetchTrigger,
  panelTriggerKey,
  onFullscreen,
  isFullscreen,
  finalAirlineId,
  /** "charges" | "messages" — manual queries often have no Gmail thread yet; use "charges" so rates UI is visible first */
  initialSectionTab = "messages",
  /** Split-pane quote layout only; set false when embedding in manual query detail (non-flex parent) */
  fillParentHeight = true,
  /** Manual query detail: rates only — no Messages tab or email thread */
  chargesOnly = false,
}) {
  const loginToken = useSelector((state) => state.reducer.userToken);
  const userLoggedIn = useSelector((state) => state.reducer.userLoggedIn);
  const quoteId = useParams().id;

  const [selectedTemplateName, setSelectedTemplateName] = useState("");
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [templateData, setTemplateData] = useState({});
  const [templatePopulating, setTemplatePopulating] = useState(false);
  const replyDraftDirtyRef = useRef(false);
  const onReplyDraftDirty = useCallback((dirty) => {
    replyDraftDirtyRef.current = dirty;
  }, []);
  const [templateSwitchModalOpen, setTemplateSwitchModalOpen] = useState(false);
  const [pendingTemplateName, setPendingTemplateName] = useState(null);
  const pendingTemplateExtraRef = useRef([]);
  const [airlines, setAirlines] = useState([]);
  const [airlineInput, setAirlineInput] = useState("");
  const [showAirlineSuggestions, setShowAirlineSuggestions] = useState(false);
  const [newAirline, setNewAirline] = useState({
    "name": "",
    "airlineCode": "",
    "email_address": "",
    "description": ""
  });
  const [emailMessages, setEmailMessages] = useState([]);
  /* Guards the thread against showing the WRONG conversation. Switching airline
     fires a new fetch while the previous one may still be in flight; without a
     generation token a slow earlier response can land after the newer one and
     repaint the previous airline's messages. */
  const threadReqRef = useRef(0);
  const [scrollOnUpdate, setScrollOnUpdate] = useState(false);

  const [threadId, setThreadId] = useState()

  const [sectionTab, setSectionTab] = useState(
    chargesOnly
      ? "charges"
      : initialSectionTab === "charges" || initialSectionTab === "messages"
        ? initialSectionTab
        : "messages"
  );

  const [initialEmail, setInitialEmail] = useState("");
  const [initialSubject, setInitialSubject] = useState("");

  const [TO_EMAIL, setTO_EMAIL] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState({});
  const [threadRefreshLoading, setThreadRefreshLoading] = useState(false);
  const [threadRefreshCooldown, setThreadRefreshCooldown] = useState(false);

  const bottomRef = useRef(null);
  const debounceRef = useRef(null);
  const airlinePartnerSelectorRef = useRef(null);
  const [airlinePartnerMenuOpen, setAirlinePartnerMenuOpen] = useState(false);
  const [airlinePartnerCreateOpen, setAirlinePartnerCreateOpen] = useState(false);
  const [airlinePartnerSaving, setAirlinePartnerSaving] = useState(false);

  useEffect(() => {
    /** Steps 6-9 of quote-detail tour land in the airline lane. See quoteDetailTour.js. */
    const onTourStep = (e) => {
      const { tourId, step } = e.detail || {};
      if (tourId !== "quote-detail" || typeof step !== "number") return;
      if (chargesOnly) return;
      if (step === 6) setSectionTab("messages"); // Reply composer
      else if (step === 7) setSectionTab("messages"); // Airline lane intro
      else if (step === 8) setSectionTab("charges"); // Tabs explanation
      else if (step === 9) setSectionTab("charges"); // Enter rates
    };
    window.addEventListener("fk:tour:step", onTourStep);
    return () => window.removeEventListener("fk:tour:step", onTourStep);
  }, [chargesOnly]);

  const my_saved_airlines_filtered = (my_saved_airlines || []).filter(a =>
    (a.airlineDetail?.name || a.airlineId)
      .toLowerCase()
      .includes((airlineInput || "").toLowerCase())
  );

  const filteredAirlines = airlines.filter(a =>
    (a.name || a._id)
      .toLowerCase()
      .includes((airlineInput || "").toLowerCase())
    && !my_saved_airlines_filtered.some(saved => (saved.airlineDetail?.name || saved.airlineId).toLowerCase() === (a.name || a._id).toLowerCase())
  );

  /** Master airlines (Admin directory), excluding any already on this quote — same idea as legacy typeahead `filteredAirlines` without text filter. */
  const directoryAirlinesForPartnerPicker = airlines.filter((a) => {
    const id = a?._id;
    if (!id) return false;
    if ((my_saved_airlines || []).some((s) => s.airlineId === id)) return false;
    const nameLower = (a.name || "").trim().toLowerCase();
    if (
      nameLower &&
      (my_saved_airlines || []).some(
        (s) =>
          (s.airlineDetail?.name || s.airlineId || "").toLowerCase() === nameLower
      )
    ) {
      return false;
    }
    return true;
  });

  const handleTemplateChange = (event, availableTemplates2 = []) => {
    const selectedName = event.target.value;
    if (selectedName === selectedTemplateName) return;
    const template = [...availableTemplates, ...availableTemplates2].find(
      (t) => t.templateName === selectedName
    );
    if (replyDraftDirtyRef.current) {
      pendingTemplateExtraRef.current = availableTemplates2;
      setPendingTemplateName(selectedName);
      setTemplateSwitchModalOpen(true);
      return;
    }
    setSelectedTemplateName(selectedName);
    if (template) fetchTemplateContent(template);
  };

  const confirmPendingTemplateSwitch = () => {
    const name = pendingTemplateName;
    const extra = pendingTemplateExtraRef.current;
    setPendingTemplateName(null);
    pendingTemplateExtraRef.current = [];
    setTemplateSwitchModalOpen(false);
    if (!name) return;
    setSelectedTemplateName(name);
    const t = [...availableTemplates, ...extra].find((x) => x.templateName === name);
    if (t) fetchTemplateContent(t);
  };

  const cancelPendingTemplateSwitch = () => {
    setPendingTemplateName(null);
    pendingTemplateExtraRef.current = [];
    setTemplateSwitchModalOpen(false);
  };

  const DataForCalculation = openPanel?.airline?.dataForCalculation || {};

  // NOTE: `openPanel.airline` is always an object (e.g. `{}` on a fresh
  // manual query, or `{ selectedAirlineId, ... }` elsewhere). The legacy
  // `openPanel["airline"]` fallback used to short-circuit on that object,
  // making selectedAirlineId an object literal `{}` — which then got POSTed
  // as `savedAirlineToUpdate` and rejected by the backend with
  // "Invalid SavedAirlineId format." Use the nested `selectedAirlineId`
  // explicitly so we always end up with a string id (or "").
  const [selectedAirlineId, setSelectedAirlineId] = useState(
    finalAirlineId ? finalAirlineId :
      DataForCalculation?.selectedAirlineId ||
      openPanel?.airline?.selectedAirlineId ||
      my_saved_airlines?.[0]?.airlineId ||
      ""
  );

  const allAirlineCodes = airlines.map(a => a.airlineCode);

  const selectedAirline = my_saved_airlines.find(
    (a) => a.airlineId === selectedAirlineId
  );
  const selectedAirlineMaster = airlines.find((a) => a._id === selectedAirlineId);
  const resolvedAirlineToEmail = (
    selectedAirline?.airlineDetail?.email_address ||
    selectedAirlineMaster?.email_address ||
    ""
  ).trim();

  // Subscribe to SSE-delivered live messages for ALL saved-airline threads on this
  // quote (not just the selected one) so each airline pane can pulse when a new
  // message arrives in its thread, even when another airline is currently open.
  const activeThreadId = selectedAirline?.emailThreadId || threadId;
  const watchedThreadIds = useMemo(
    () =>
      (my_saved_airlines || [])
        .map((a) => a.emailThreadId)
        .filter(Boolean),
    [my_saved_airlines]
  );
  const liveMessagesByThread = useSelector((state) => {
    const map = state.threads.liveMessages || {};
    const result = {};
    for (const tid of watchedThreadIds) {
      if (map[tid]) result[tid] = map[tid];
    }
    if (activeThreadId && map[activeThreadId] && !result[activeThreadId]) {
      result[activeThreadId] = map[activeThreadId];
    }
    return result;
  }, shallowEqual);
  const liveMessages = activeThreadId
    ? liveMessagesByThread[activeThreadId] || []
    : [];
  const combinedAirlineMessages = activeThreadId
    ? sortThreadMessages([...emailMessages, ...liveMessages])
    : emailMessages;

  // Per-airline pulse state — keyed by airlineId. Auto-clears 2.8s after a new
  // SSE message lands in a thread the operator isn't currently looking at.
  const [airlinePulseMap, setAirlinePulseMap] = useState({});
  const prevLiveLensRef = useRef({});
  const pulseTimersRef = useRef({});
  useEffect(() => {
    const prev = prevLiveLensRef.current;
    const next = { ...prev };
    (my_saved_airlines || []).forEach((a) => {
      const tid = a.emailThreadId;
      if (!tid) return;
      const len = (liveMessagesByThread[tid] || []).length;
      const prevLen = prev[tid] || 0;
      next[tid] = len;
      if (len > prevLen && a.airlineId !== selectedAirlineId) {
        const id = a.airlineId;
        setAirlinePulseMap((p) => ({ ...p, [id]: true }));
        if (pulseTimersRef.current[id]) clearTimeout(pulseTimersRef.current[id]);
        pulseTimersRef.current[id] = setTimeout(() => {
          setAirlinePulseMap((p) => {
            const cp = { ...p };
            delete cp[id];
            return cp;
          });
        }, 2800);
      }
    });
    prevLiveLensRef.current = next;
  }, [liveMessagesByThread, my_saved_airlines, selectedAirlineId]);
  useEffect(
    () => () => {
      Object.values(pulseTimersRef.current).forEach((t) => clearTimeout(t));
      pulseTimersRef.current = {};
    },
    []
  );
  // Selecting an airline (via any path — partner menu, openPanel, etc.) clears
  // its pending pulse so the indicator doesn't linger after the operator has
  // already navigated to the thread.
  useEffect(() => {
    if (!selectedAirlineId) return;
    setAirlinePulseMap((p) => {
      if (!p[selectedAirlineId]) return p;
      const cp = { ...p };
      delete cp[selectedAirlineId];
      return cp;
    });
    if (pulseTimersRef.current[selectedAirlineId]) {
      clearTimeout(pulseTimersRef.current[selectedAirlineId]);
      delete pulseTimersRef.current[selectedAirlineId];
    }
  }, [selectedAirlineId]);
  const anyAirlinePulse = Object.values(airlinePulseMap).some(Boolean);
  const [finalRates, setFinalRates] = useState(my_saved_airlines?.[0] ? transformData({
    ...my_saved_airlines.map(airline => {
      return ({
        [airline.airlineId]: {
          currentRate: Number(airline?.finalRate) || 0,
          transitTime: airline?.transitTime != null ? String(airline.transitTime) : "",
          minCurrentRate: Number(airline?.minCharge) || 0,
          PrimaryScreening: Number(airline?.screeningPrices?.primaryScreeningPrice?.ratePerVolumeWeight || airline?.screeningPrices?.primaryScreeningPrice) || 0,
          minPrimaryScreening: Number(airline?.screeningPrices?.primaryScreeningPrice?.minCharge) || 0,
          SecondaryScreening: Number(airline?.screeningPrices?.secondaryScreeningPrice?.ratePerVolumeWeight || airline?.screeningPrices?.secondaryScreeningPrice) || 0,
          minSecondaryScreening: Number(airline?.screeningPrices?.secondaryScreeningPrice?.minCharge) || 0,
          Fuel: Number(airline?.FFWH?.fuelSurcharge?.ratePerVolumeWeight || airline?.FFWH?.fuelSurcharge) || 0,
          Freight: Number(airline?.FFWH?.freightCharge?.ratePerVolumeWeight || airline?.FFWH?.freightCharge) || 0,
          minFreight: Number(airline?.FFWH?.freightCharge?.minCharge) || 0,
          War: Number(airline?.FFWH?.warRiskSurcharge?.ratePerVolumeWeight || airline?.FFWH?.warRiskSurcharge) || 0,
          minWar: Number(airline?.FFWH?.warRiskSurcharge?.minCharge) || 0,
          Handling: Number(airline?.FFWH?.handlingFee?.ratePerVolumeWeight || airline?.FFWH?.handlingFee) || 0,
          minHandling: Number(airline?.FFWH?.handlingFee?.minCharge) || 0,
          OriginAirports: Array(...(airline?.originAirports ? airline.originAirports : [])) || []
        }
      })
    })
  }) : {});


  function transformData(data) {
    const result = {};
    for (const outerKey in data) {
      const innerObj = data[outerKey];
      const [innerKey, values] = Object.entries(innerObj)[0];
      result[innerKey] = values;
    }
    return result;
  }

  // ---------------------------------------------------------------------------
  // Per-message AI assist + Extract-rates popover. Replaces the old top-of-thread
  // "Parse w/ AI" header button + AirlineAiSummaryPanel pair: operator clicks the
  // wand on the airline reply they want to extract, the popover offers Summary /
  // Suggested reply (shared shape with customer flow) plus an "Extract rates"
  // tab that calls parseAirlineRateFromInput and applies via updateSavedAirlineRate.
  // ---------------------------------------------------------------------------
  const [assistOpenForMessageId, setAssistOpenForMessageId] = useState(null);
  const [assistAnchorRect, setAssistAnchorRect] = useState(null);
  const [assistLoading, setAssistLoading] = useState(false);
  const [assistError, setAssistError] = useState(null);
  const [assistData, setAssistData] = useState({
    summary: null,
    suggestedReply: null,
    cached: false,
    generatedAt: null,
  });
  const [extractLoading, setExtractLoading] = useState(false);
  const [extractError, setExtractError] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [extractedWanted, setExtractedWanted] = useState(null);
  const [applying, setApplying] = useState(false);

  const closeAssist = useCallback(() => {
    setAssistOpenForMessageId(null);
    setAssistAnchorRect(null);
    setAssistError(null);
    setAssistLoading(false);
    setExtractLoading(false);
    setExtractError(null);
    setExtractedData(null);
    setExtractedWanted(null);
    setApplying(false);
  }, []);

  const handleAssistRegenerate = useCallback(async () => {
    if (!assistOpenForMessageId) return;
    if (!quoteId || !loginToken || !userLoggedIn?.role) return;
    setAssistLoading(true);
    setAssistError(null);
    try {
      const res = await postQuoteMessageAssist({
        loginToken,
        role: userLoggedIn.role,
        quoteId,
        messageId: assistOpenForMessageId,
        threadId: selectedAirline?.emailThreadId || threadId,
        mode: "both",
        force: true,
      });
      setAssistData({
        summary: res.summary ?? null,
        suggestedReply: res.suggestedReply ?? null,
        cached: Boolean(res.cached),
        generatedAt:
          res.generatedAt != null
            ? new Date(res.generatedAt).toLocaleString()
            : null,
      });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        toast.error("Too many assist requests. Try again in about a minute.");
        setAssistError("Too many requests. Please wait a minute.");
      } else {
        const msg =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Assist failed";
        setAssistError(typeof msg === "string" ? msg : "Assist failed");
      }
    } finally {
      setAssistLoading(false);
    }
  }, [
    assistOpenForMessageId,
    quoteId,
    loginToken,
    userLoggedIn?.role,
    selectedAirline?.emailThreadId,
    threadId,
  ]);

  const handleAssistCopy = useCallback(async (text) => {
    try {
      await navigator.clipboard.writeText(String(text || ""));
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Could not copy");
    }
  }, []);

  const handleMessageAssist = useCallback(
    async (messageId, anchorEl) => {
      const rect = anchorEl?.getBoundingClientRect?.();
      setAssistAnchorRect(
        rect
          ? {
            top: rect.top,
            left: rect.left,
            right: rect.right,
            bottom: rect.bottom,
            width: rect.width,
            height: rect.height,
          }
          : null
      );
      setAssistOpenForMessageId(messageId);
      setAssistError(null);
      setAssistData({
        summary: null,
        suggestedReply: null,
        cached: false,
        generatedAt: null,
      });
      // Reset extract state for this fresh open.
      setExtractError(null);
      setExtractedData(null);
      setExtractedWanted(null);
      setExtractLoading(false);
      setApplying(false);

      if (!quoteId || !loginToken || !userLoggedIn?.role) {
        setAssistError("Missing quote or session");
        return;
      }
      setAssistLoading(true);
      try {
        const res = await postQuoteMessageAssist({
          loginToken,
          role: userLoggedIn.role,
          quoteId,
          messageId,
          threadId: selectedAirline?.emailThreadId || threadId,
          mode: "both",
          force: false,
        });
        setAssistData({
          summary: res.summary ?? null,
          suggestedReply: res.suggestedReply ?? null,
          cached: Boolean(res.cached),
          generatedAt:
            res.generatedAt != null
              ? new Date(res.generatedAt).toLocaleString()
              : null,
        });
      } catch (err) {
        const status = err?.response?.status;
        if (status === 429) {
          toast.error("Too many assist requests. Try again in about a minute.");
          setAssistError("Too many requests. Please wait a minute.");
        } else {
          const msg =
            err?.response?.data?.error ||
            err?.response?.data?.message ||
            err?.message ||
            "Assist failed";
          setAssistError(typeof msg === "string" ? msg : "Assist failed");
        }
      } finally {
        setAssistLoading(false);
      }
    },
    [
      quoteId,
      loginToken,
      userLoggedIn?.role,
      // selectedAirline.emailThreadId is needed inside the closure to route
      // the assist call to the right Gmail thread. Without it the callback
      // captures the value from first render (usually undefined) and the
      // backend falls back to q.gmailThreadId → message_not_found.
      selectedAirline?.emailThreadId,
      threadId,
    ]
  );

  // Resolve the plain-text body for the message the popover is anchored to —
  // this is what we feed to parseAirlineRateFromInput.
  const assistMessageText = useMemo(() => {
    if (!assistOpenForMessageId) return "";
    const msg = (combinedAirlineMessages || []).find(
      (m) => (m.id || m.messageId) === assistOpenForMessageId
    );
    if (!msg) return "";
    return stripHtmlToText(msg.html || "");
  }, [assistOpenForMessageId, combinedAirlineMessages]);

  const handleExtract = useCallback(async () => {
    if (!selectedAirline?._id) {
      toast.error("Select an airline first.");
      return;
    }
    if (!assistMessageText) {
      toast.error("No message text available to extract from.");
      return;
    }
    setExtractError(null);
    setExtractedData(null);
    setExtractedWanted(null);
    setExtractLoading(true);
    try {
      const res = await parseAirlineRateFromInput({
        loginToken,
        role: userLoggedIn?.role,
        savedAirlineId: selectedAirline._id,
        messageId: assistOpenForMessageId || undefined,
        text: assistMessageText,
        force: false,
      });
      if (res?.empty) {
        setExtractError(
          res?.message ||
          "AI didn't find rate values in this message. Try a different message in the thread."
        );
        setExtractedData(null);
        setExtractedWanted(null);
      } else {
        setExtractedData(res?.data || null);
        setExtractedWanted(res?.wanted || null);
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        toast.error("Too many parse requests. Try again in about a minute.");
        setExtractError("Too many requests. Please wait a minute.");
      } else {
        const msg =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Extract failed";
        setExtractError(typeof msg === "string" ? msg : "Extract failed");
      }
    } finally {
      setExtractLoading(false);
    }
  }, [
    selectedAirline?._id,
    assistMessageText,
    assistOpenForMessageId,
    loginToken,
    userLoggedIn?.role,
  ]);

  const handleApply = useCallback(
    async (parsed) => {
      if (!selectedAirline) {
        toast.error("Saved airline not found.");
        return;
      }
      const data = parsed || extractedData || {};
      const wanted = extractedWanted || null;

      // Mirror the consumer's freight fallback (emailConsumer.js ~1697-1745):
      // 1) FFWH.freightCharge.per_kg (or its base_freight alias), else
      // 2) wanted-rate per_kg, else
      // 3) rates.general.per_kg, else
      // 4) first non-zero per_kg across rates.{non-stackable,stackable,hazardous,mix}.
      const freightCharge = data?.FFWH?.freightCharge || {};
      const ffwhFreightPerKg = Number(
        freightCharge.per_kg ?? freightCharge.base_freight ?? 0
      );
      const ffwhFreightMin = Number(freightCharge.min_charge ?? 0);
      const wantedPerKg = Number(wanted?.per_kg ?? 0);
      const wantedMin = Number(wanted?.min_charge ?? 0);
      const rates = data?.rates || {};
      const generalPerKg = Number(rates?.general?.per_kg ?? 0);
      const generalMin = Number(rates?.general?.min_charge ?? 0);

      let finalRate = 0;
      let minCharge = 0;
      if (ffwhFreightPerKg > 0) {
        finalRate = ffwhFreightPerKg;
        minCharge = ffwhFreightMin;
      } else if (wantedPerKg > 0) {
        finalRate = wantedPerKg;
        minCharge = wantedMin;
      } else if (generalPerKg > 0) {
        finalRate = generalPerKg;
        minCharge = generalMin;
      } else {
        for (const rateType of ["non-stackable", "stackable", "hazardous", "mix"]) {
          const rt = rates?.[rateType];
          if (Number(rt?.per_kg ?? 0) > 0) {
            finalRate = Number(rt.per_kg);
            minCharge = Number(rt.min_charge ?? 0);
            break;
          }
        }
      }

      const sp = data?.screeningPrices || {};
      const ffwh = data?.FFWH || {};
      const screeningPricesBody = {};
      if (sp.primaryScreeningPrice) {
        screeningPricesBody.primaryScreeningPrice = {
          ratePerVolumeWeight: Number(sp.primaryScreeningPrice.per_kg ?? 0),
          minCharge: Number(sp.primaryScreeningPrice.min_charge ?? 0),
        };
      }
      if (sp.secondaryScreeningPrice) {
        screeningPricesBody.secondaryScreeningPrice = {
          ratePerVolumeWeight: Number(sp.secondaryScreeningPrice.per_kg ?? 0),
          minCharge: Number(sp.secondaryScreeningPrice.min_charge ?? 0),
        };
      }

      const ffwhBody = {};
      if (ffwh.fuelSurcharge) {
        const fs = ffwh.fuelSurcharge;
        ffwhBody.fuelSurcharge =
          typeof fs === "object"
            ? Number(fs.per_kg ?? fs.ratePerVolumeWeight ?? 0)
            : Number(fs) || 0;
      }
      if (ffwh.freightCharge) {
        ffwhBody.freightCharge = {
          ratePerVolumeWeight: Number(
            ffwh.freightCharge.per_kg ?? ffwh.freightCharge.ratePerVolumeWeight ?? 0
          ),
          minCharge: Number(
            ffwh.freightCharge.min_charge ?? ffwh.freightCharge.minCharge ?? 0
          ),
        };
      }
      if (ffwh.warRiskSurcharge) {
        ffwhBody.warRiskSurcharge = {
          ratePerVolumeWeight: Number(
            ffwh.warRiskSurcharge.per_kg ??
            ffwh.warRiskSurcharge.ratePerVolumeWeight ??
            0
          ),
          minCharge: Number(
            ffwh.warRiskSurcharge.min_charge ?? ffwh.warRiskSurcharge.minCharge ?? 0
          ),
        };
      }
      if (ffwh.handlingFee) {
        ffwhBody.handlingFee = {
          ratePerVolumeWeight: Number(
            ffwh.handlingFee.per_kg ?? ffwh.handlingFee.ratePerVolumeWeight ?? 0
          ),
          minCharge: Number(
            ffwh.handlingFee.min_charge ?? ffwh.handlingFee.minCharge ?? 0
          ),
        };
      }

      const transitTimeRaw = ffwh.transitTimeDays;
      const transitTime =
        transitTimeRaw != null && String(transitTimeRaw).trim() !== ""
          ? String(transitTimeRaw)
          : selectedAirline.transitTime != null
            ? String(selectedAirline.transitTime)
            : "";

      const body = {
        token: loginToken,
        role: userLoggedIn?.role,
        quoteId: selectedAirline.quoteId || quoteId,
        savedAirlineToUpdate: selectedAirline.airlineId,
        finalRate: Number(finalRate) || 0,
        minCharge: Number(minCharge) || 0,
        ...(Object.keys(screeningPricesBody).length
          ? { screeningPrices: screeningPricesBody }
          : {}),
        ...(Object.keys(ffwhBody).length ? { FFWH: ffwhBody } : {}),
        transitTime,
        /* The extract already reads both of these off the airline's reply — the
           popover even shows "Validity" as a chip — and neither was carried into
           the save. So every stored rate looked like a live sterling rate, and
           the cost table had nothing to warn on. An unreadable date is left out
           rather than stored as a broken one. */
        ...(() => {
          const code = String(data?.currency || "").trim().toUpperCase();
          return code ? { currency: code } : {};
        })(),
        ...(() => {
          const raw = data?.valid_until;
          if (!raw) return {};
          const d = new Date(raw);
          return Number.isNaN(d.getTime())
            ? {}
            : { rateValidityUntil: d.toISOString() };
        })(),
      };

      if (
        ffwhBody.fuelSurcharge !== undefined &&
        (ffwhBody.fuelSurcharge < 0 || ffwhBody.fuelSurcharge > 100)
      ) {
        toast.error(
          `Fuel surcharge must be between 0–100% (got ${ffwhBody.fuelSurcharge}%). Please correct the value and try again.`
        );
        return;
      }

      const war = ffwhBody.warRiskSurcharge;
      if (war && war.ratePerVolumeWeight > 0 && war.minCharge > 0 && war.minCharge < war.ratePerVolumeWeight * 2) {
        toast.error(
          `War risk min charge (${war.minCharge}) must be ≥ 2× war risk surcharge rate (${war.ratePerVolumeWeight * 2}).`
        );
        return;
      }

      const handling = ffwhBody.handlingFee;
      if (handling && handling.ratePerVolumeWeight > 0 && handling.minCharge > 0 && handling.minCharge < handling.ratePerVolumeWeight * 2) {
        toast.error(
          `Handling fee min charge (${handling.minCharge}) must be ≥ 2× handling fee rate (${handling.ratePerVolumeWeight * 2}).`
        );
        return;
      }

      setApplying(true);
      try {
        await updateSavedAirlineRate(body);
        toast.success("Airline rate updated from AI extract.");
        // finalRates is a local state that was seeded once from my_saved_airlines
        // and never re-syncs from the refetched prop (React preserves state
        // across re-renders). So even though the backend update + refetch
        // succeed, the Charges & details tab keeps showing the seed values.
        // Mirror the freshly-applied values into finalRates immediately so the
        // tab reflects the AI-extracted numbers without waiting on remount.
        const airlineKey = selectedAirline.airlineId;
        const screening = body.screeningPrices || {};
        const ffwhVals = body.FFWH || {};
        setFinalRates((prev) => ({
          ...(prev || {}),
          [airlineKey]: {
            ...(prev?.[airlineKey] || {}),
            currentRate: Number(body.finalRate) || 0,
            minCurrentRate: Number(body.minCharge) || 0,
            transitTime: body.transitTime || "",
            PrimaryScreening: Number(
              screening.primaryScreeningPrice?.ratePerVolumeWeight ?? 0
            ),
            minPrimaryScreening: Number(
              screening.primaryScreeningPrice?.minCharge ?? 0
            ),
            SecondaryScreening: Number(
              screening.secondaryScreeningPrice?.ratePerVolumeWeight ?? 0
            ),
            minSecondaryScreening: Number(
              screening.secondaryScreeningPrice?.minCharge ?? 0
            ),
            Fuel: Number(
              typeof ffwhVals.fuelSurcharge === "object"
                ? ffwhVals.fuelSurcharge?.ratePerVolumeWeight ?? 0
                : ffwhVals.fuelSurcharge ?? 0
            ),
            Freight: Number(ffwhVals.freightCharge?.ratePerVolumeWeight ?? 0),
            minFreight: Number(ffwhVals.freightCharge?.minCharge ?? 0),
            War: Number(ffwhVals.warRiskSurcharge?.ratePerVolumeWeight ?? 0),
            minWar: Number(ffwhVals.warRiskSurcharge?.minCharge ?? 0),
            Handling: Number(ffwhVals.handlingFee?.ratePerVolumeWeight ?? 0),
            minHandling: Number(ffwhVals.handlingFee?.minCharge ?? 0),
          },
        }));
        if (typeof setFqctTrigger === "function") {
          setFqctTrigger((p) => p + 1);
        }
        closeAssist();
      } catch (err) {
        const msg =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Failed to apply extracted rate";
        toast.error(typeof msg === "string" ? msg : "Failed to apply extracted rate");
      } finally {
        setApplying(false);
      }
    },
    [
      selectedAirline,
      extractedData,
      extractedWanted,
      loginToken,
      userLoggedIn?.role,
      quoteId,
      setFqctTrigger,
      closeAssist,
      setFinalRates,
    ]
  );

  // Close the popover when the operator switches airlines.
  useEffect(() => {
    closeAssist();
  }, [selectedAirlineId, closeAssist]);

  const scrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useLayoutEffect(() => {
    if (scrollOnUpdate) {
      isOpen && scrollToBottom();
      setScrollOnUpdate(false);
    }
  }, [emailMessages, scrollOnUpdate, isOpen]);

  useEffect(() => {
    const fetchData = async () => {
      let filteredTemplates
      let X = ["NS:", "QS:", "BS:"]
      let pattern = X.reduce((acc, x, i) => acc + (i > 0 ? "|" : "") + x, "");
      try {
        const airlineResponse = await getAllAirlines({ token: loginToken, role: userLoggedIn?.role, limit: 1000 });
        setAirlines(airlineResponse?.airlines || []);
        const AllTemplates = await getListOfTemplates({
          token: loginToken,
          role: userLoggedIn?.role,
          search: pattern,
          searchOnField: "templateName",
          for: "airline",
        });
        filteredTemplates = AllTemplates?.emailTemplates || [];
        setAvailableTemplates(filteredTemplates);
        if (openPanel?.airline?.selectTemplate) {
          applyForcedTemplate(
            openPanel.airline.selectTemplate,
            filteredTemplates
          );
        }
      } catch {
      } finally {
        setLoading(false);
      }
    };
    if (loginToken && quoteId) {
      fetchData();
    }
  }, [loginToken, quoteId, panelTriggerKey]);

  const applyForcedTemplate = (templatePattern, templates) => {
    const template = templates.find((t) => t.purpose === templatePattern);
    if (template) {
      setSelectedTemplateName(template.templateName);
      fetchTemplateContent(template);
    }
  };

  const fetchTemplateContent = async (template) => {
    if (!template || !template.purpose || !template.for) return;

    setTemplatePopulating(true);
    try {
      const templateResponse = await emailTemplatePopulated({
        token: loginToken,
        quoteId,
        role: userLoggedIn?.role,
        purpose: template.purpose,
        for: template.for,
      });
      let responseEmail = templateResponse?.emailContent || {};
      // if (responseEmail?.message?.includes(["Cost_Breakdown"])) {
      //   const parts = responseEmail.message.split("[Cost_Breakdown]");
      //   responseEmail.message = `${parts[0]} ${renderCostBreakdown(DataForCalculation)} ${parts[1] ?? ""}`;
      // }
      if (responseEmail?.message?.includes(["Payable"])) {
        const parts = responseEmail.message.split("[Payable]");
        responseEmail.message = `${parts[0]} ${highlightedPayable(DataForCalculation)} ${parts[1] ?? ""}`;
      }
      if (emailMessages.length === 0) {
        setInitialSubject(responseEmail.subject);
        setInitialEmail(responseEmail.message);
      }
      setTemplateData(responseEmail);
    } catch (err) {
      console.error("Error fetching template content:", err);
    } finally {
      setTemplatePopulating(false);
    }
  };

  useEffect(() => {
    if (!availableTemplates.length) return;
    if (openPanel?.airline?.selectTemplate) return;
    if (selectedAirline?.emailThreadId || threadId) return;
    if (emailMessages.length > 0) return;
    if (selectedTemplateName) return;
    const first = availableTemplates[0];
    if (first?.templateName) {
      setSelectedTemplateName(first.templateName);
      fetchTemplateContent(first);
    }
  }, [
    availableTemplates,
    openPanel?.airline?.selectTemplate,
    selectedAirline?.emailThreadId,
    threadId,
    emailMessages.length,
    selectedTemplateName,
  ]);

  const fetchAndSetThread = useCallback(
    async (customThreadId = null, opts = {}) => {
      const quiet = Boolean(opts?.quiet);
      const effectiveThreadId =
        customThreadId || selectedAirline?.emailThreadId || threadId;

      if (!effectiveThreadId) {
        if (!quiet) setEmailMessages([]);
        return;
      }

      const reqId = ++threadReqRef.current;

      if (quiet) setThreadRefreshLoading(true);
      else {
        setLoading(true);
        setErrorMsg("");
      }

      try {
        const res = await fetchEmailThread({
          token: loginToken,
          threadId: effectiveThreadId,
          userEmail: USER_EMAIL,
          role: userLoggedIn?.role,
        });

        // A newer selection superseded this request while it was in flight.
        if (reqId !== threadReqRef.current) return;
        setTO_EMAIL(resolvedAirlineToEmail);
        const parsed = (res?.messages || []).map(parseEmailMessage);
        setEmailMessages(parsed);
        if (parsed.length > 0) {
          setSelectedTemplateName("");
          setTemplateData({});
        }
        setLastUpdated(new Date());
      } catch (err) {
        console.error("Failed to fetch thread:", err);
        const detail =
          err?.response?.data?.details ||
          err?.response?.data?.error ||
          err?.message ||
          "Unknown error";
        if (!quiet) {
          // Never leave the PREVIOUS airline's messages on screen under the new
          // one — a failed load read as "this airline has that conversation",
          // which is worse than showing nothing.
          if (reqId === threadReqRef.current) setEmailMessages([]);
          setErrorMsg(`Failed to load conversation: ${detail}`);
        } else {
          throw err;
        }
      } finally {
        if (quiet) setThreadRefreshLoading(false);
        else setLoading(false);
      }
    },
    [loginToken, selectedAirline?.emailThreadId, threadId, resolvedAirlineToEmail]
  );

  const handleManualThreadRefresh = useCallback(async () => {
    if (!selectedAirline?.emailThreadId && !threadId) return;
    if (threadRefreshLoading || threadRefreshCooldown) return;
    if (chargesOnly) return;
    try {
      await fetchAndSetThread(null, { quiet: true });
      setFqctTrigger?.((p) => p + 1);
      toast.success("Thread refreshed");
      setThreadRefreshCooldown(true);
      setTimeout(() => setThreadRefreshCooldown(false), 3000);
    } catch (e) {
      console.error("Could not refresh airline email thread", e);
      const detail =
        e?.response?.data?.details ||
        e?.response?.data?.error ||
        e?.message ||
        "Unknown error";
      toast.error(`Could not refresh thread: ${detail}`);
    }
  }, [
    selectedAirline?.emailThreadId,
    threadId,
    threadRefreshLoading,
    threadRefreshCooldown,
    chargesOnly,
    fetchAndSetThread,
    setFqctTrigger,
  ]);

  const handleInitialEmailSend = async (replyHtml) => {
    if (!initialEmail || !initialSubject) {
      setErrorMsg("Subject and message are required.");
      return;
    }
    setLoading(true);
    setErrorMsg("");

    try {
      const res = await sendEmail({
        token: loginToken,
        to: resolvedAirlineToEmail || undefined,
        // TESTING ONLY — deliberately pinned so test sends can't leave this
        // inbox. Must become `USER_EMAIL` (the quote's receivedInInbox) before
        // production: a fixed sender mails one tenant's correspondence as
        // another (tracker G3).
        from: "shawak@vantagecargo.com",
        role: userLoggedIn?.role,
        selectedAirlineId: selectedAirlineId,
        subject: initialSubject,
        body: replyHtml,
        entityType: "airline",
        // The carrier's NAME — see the transport pane for why the entity type
        // here rendered as "Airline airline replied".
        entityName: selectedAirline?.airlineDetail?.name || "Airline",
        quoteId,
      });

      if (res?.threadId) {
        setThreadId(res.threadId)
        // Was: `selectedAirline.emailThreadId = res.threadId` — mutating a prop
        // object in place. React never sees it, so nothing re-renders from it,
        // and it quietly writes into the parent's state. The backend already
        // persisted the thread onto the saved airline (draftController ->
        // airlineUpdateThreadId); refetch so the prop arrives correctly instead
        // of forging it locally.
        setRefetchTrigger?.((prev) => prev + 1);
        await fetchAndSetThread(res.threadId);
        setPanelTriggerKey((prev) => (prev + 1))
      } else {
        throw new Error("Thread ID not returned");
      }
    } catch (err) {
      console.error("❌ Failed to send initial email:", err);
      setErrorMsg("Failed to start conversation. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleReplySubmit = async (replyHtml, attachments = [], recipients = {}) => {
    const latest = emailMessages?.[0];
    const allReferences = emailMessages
      .map((msg) => msg.messageId)
      .filter(Boolean)
      .reverse()
      .join(" ");

    try {
      // Ensure 'to' is an array with at least one valid email
      const toRecipients = Array.isArray(recipients.to)
        ? recipients.to.filter(email => email && email.trim())
        : (recipients.to ? [recipients.to] : resolvedAirlineToEmail ? [resolvedAirlineToEmail] : []);

      if (toRecipients.length === 0) {
        throw new Error('At least one recipient is required');
      }

      await replyToEmailThread({
        token: loginToken,
        threadId: selectedAirline?.emailThreadId,
        to: toRecipients,
        // TODO: CC and BCC temporarily disabled - not fully implemented yet
        cc: [], // CC temporarily disabled
        bcc: [], // BCC temporarily disabled
        subject: latest?.subject || null,
        body: replyHtml,
        from: USER_EMAIL,
        inReplyTo: latest?.messageId,
        references: allReferences,
        role: userLoggedIn?.role,
        attachments,
      });
      await fetchAndSetThread();
    } catch (err) {
      console.error("Failed to send reply", err);
      setErrorMsg("Failed to send email. Please try again.");
    }
  };

  useEffect(() => {
    // Blank the thread the moment the selection changes, before the fetch
    // resolves. Otherwise the previous airline's messages stay visible under
    // the new name for the whole debounce + round trip.
    setEmailMessages([]);
    setErrorMsg("");
    // Also drop the component-level threadId. fetchAndSetThread resolves
    // `customThreadId || selectedAirline?.emailThreadId || threadId`, and that last
    // fallback still held the PREVIOUS airline\'s thread — so a airline
    // with no conversation of its own would show someone else\'s, including a
    // bounce, and the composer would address it.
    setThreadId(null);
    if (!loginToken || !selectedAirline?.emailThreadId) {

      if (selectedAirline?.quoteId) {
        // Show add airline button
      }
      setEmailMessages([]);
      return
    };

    setErrorMsg("");
    setLoading(true);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchAndSetThread();
    }, 300);
    // Track the THREAD, not just the airline id. Keyed only on selectedAirlineId
    // this never refired when the selected airline's emailThreadId changed —
    // e.g. a thread created/reassigned after the first send — so the pane kept
    // showing the previous thread. TransportConversation keys on the thread id
    // for exactly this reason; the two panes now behave identically.
  }, [loginToken, selectedAirlineId, selectedAirline?.emailThreadId]);

  // Register every saved-airline thread for SSE so the per-airline pulse keeps
  // working when the operator is parked on a different airline. Refires only
  // when the watched-thread list itself changes (not on every render).
  const watchedThreadKey = watchedThreadIds.join(",");
  useEffect(() => {
    if (!loginToken || !watchedThreadIds.length) return;
    watchedThreadIds.forEach((tid) => {
      registerWatchedThread({
        token: loginToken,
        threadId: tid,
        role: userLoggedIn?.role,
      }).catch((err) =>
        console.error("Failed to register airline thread for SSE:", err)
      );
    });
  }, [loginToken, watchedThreadKey, userLoggedIn?.role]);

  // Track the last highlightThreadId we already reacted to. Without this the
  // effect re-fires whenever my_saved_airlines updates (e.g. after a refetch
  // triggered by an action in another pane), which would yank the operator
  // out of the pane they're working in and back into airlines.
  const lastHandledHighlightRef = useRef(null);
  useEffect(() => {
    if (!highlightThreadId) return;
    if (lastHandledHighlightRef.current === highlightThreadId) return;

    const airlineMatch = my_saved_airlines.find(
      (a) => a.emailThreadId === highlightThreadId
    );

    if (airlineMatch) {
      lastHandledHighlightRef.current = highlightThreadId;
      setSelectedAirlineId(airlineMatch.airlineId);
      setIsOpen(true);
      fetchAndSetThread();
    }
  }, [highlightThreadId, my_saved_airlines, selectedAirlineId]);

  const createNewAirline = async (airlineDataOverride) => {
    const airlineToSave = airlineDataOverride || newAirline;

    if (!airlineToSave.name || !airlineToSave.airlineCode || !airlineToSave.email_address) {
      showSwal("Failure", "Please provide airline name, unique code, and email.", 200);
      return null;
    }
    try {
      const res = await addNewAirline({
        token: loginToken,
        data: airlineToSave,
        role: userLoggedIn?.role,
      });
      showSwal("Success", res.message, 200);
      return res.airline;
    } catch (err) {
      console.error("Failed to create airline", err);
      return null
    }
  };

  const saveMyNewAirlineWithQuote = async (airline) => {
    const type = "airline";
    try {
      if (airline?.email_address && quoteId) {
        const payload = {
          "quoteId": quoteId,
          "airlineId": airline._id,
          "richEmail": {
            "subject": "Request for new Rate",
            "body": "<p>Request for new Rate</p>"
          },
          "pricingCondition": "general"
        }
        const response = await saveDraftEmail({
          token: loginToken,
          quoteId: quoteId,
          airlineId: airline._id,
          role: userLoggedIn?.role,
          [type === "airline" ? "saveAirlines" : "saveAgents"]: [payload],
          type,
        });

        if (response.status === 200) {
          showSwal("Success", response.message || "Airline Saved Successfully", response.status);
        } else {
          showSwal("Failure", response.message || "Email Save Failure", "error");
        }
      }
    } catch (error) {
      console.error("Failed to save new airline with quote", error);
      showSwal("Failure", "Failed to save new airline with quote. Please try again.", 200);
    }
  }

  useEffect(() => {
    if (DataForCalculation?.selectedAirlineId) {
      setSelectedAirlineId(DataForCalculation.selectedAirlineId);
    }
  }, [DataForCalculation?.selectedAirlineId]);

  // Auto-select airline when openPanel.airline.selectedAirlineId is set (e.g., from notification click)
  useEffect(() => {
    if (openPanel?.airline?.selectedAirlineId && openPanel.airline.selectedAirlineId !== selectedAirlineId) {
      const airlineId = openPanel.airline.selectedAirlineId;
      setSelectedAirlineId(airlineId);

      // Also update airlineInput to show the airline name
      const matchingAirline = my_saved_airlines.find(a => a.airlineId === airlineId);
      if (matchingAirline?.airlineDetail?.name) {
        setAirlineInput(matchingAirline.airlineDetail.name);
      }
    }
  }, [openPanel?.airline?.selectedAirlineId, my_saved_airlines]);

  useEffect(() => {
    if (!airlinePartnerMenuOpen) return;
    const onPointerDown = (e) => {
      if (
        airlinePartnerSelectorRef.current &&
        !airlinePartnerSelectorRef.current.contains(e.target)
      ) {
        setAirlinePartnerMenuOpen(false);
        setAirlinePartnerCreateOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setAirlinePartnerMenuOpen(false);
        setAirlinePartnerCreateOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [airlinePartnerMenuOpen]);

  const handleCreateAndSaveAirlineToQuote = async () => {
    if (airlinePartnerSaving) return;
    setAirlinePartnerSaving(true);
    try {
      const airlineName = airlineInput.trim().toLowerCase();
      const matchedAirline = airlines.find(
        (a) => (a.name || a._id).toLowerCase() === airlineName
      );
      let TheAirlineToSave = null;
      if (!matchedAirline) {
        const airlineToSave = {
          ...newAirline,
          name: airlineInput.trim(),
        };
        const myNewAirline = await createNewAirline(airlineToSave);
        if (myNewAirline) {
          TheAirlineToSave = myNewAirline;
          setAirlines((prev) => [...prev, myNewAirline]);
        }
      } else {
        TheAirlineToSave = matchedAirline;
      }
      if (TheAirlineToSave) {
        await saveMyNewAirlineWithQuote(TheAirlineToSave);
        setNewAirline({ name: "", airlineCode: "", email_address: "", description: "" });
        setAirlineInput("");
        setRefetchTrigger((prev) => prev + 1);
        setSelectedAirlineId(TheAirlineToSave._id);
        setAirlinePartnerMenuOpen(false);
        setAirlinePartnerCreateOpen(false);
      } else {
        showSwal("Failure", "Failed to create airline. Please try again.", 400);
      }
    } catch (err) {
      console.error(err);
      showSwal("Failure", "Failed to save airline. Please try again.", 400);
    } finally {
      setAirlinePartnerSaving(false);
    }
  };

  const createAirlineSubmitDisabled =
    !airlineInput.trim() ||
    my_saved_airlines.some(
      (a) =>
        (a.airlineDetail?.name || a.airlineId).toLowerCase() ===
        airlineInput.trim().toLowerCase()
    );

  const airlineTemplateComposerAddon = (
    <div className="relative space-y-1.5">
      {templatePopulating ? (
        <div
          className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/70 backdrop-blur-sm dark:bg-[oklch(0.225_0.026_264/0.75)]"
          aria-busy="true"
          aria-label="Loading template"
        >
          <div className="flex flex-col items-center gap-2">
            <AnimatedLoader variant="docbuild" size={72} label="Loading template" />
            <span className="text-xs font-medium text-slate-600 dark:text-slate-300">Loading template…</span>
          </div>
        </div>
      ) : null}
      <label
        htmlFor="template-select-airline"
        className="block text-sm font-bold text-gray-700 transition-colors duration-300 dark:text-gray-200"
      >
        Select Email Template:
      </label>
      <select
        id="template-select-airline"
        className="w-full rounded border border-gray-300 bg-white px-3 py-2 leading-tight text-gray-700 shadow focus:outline-none focus:shadow-outline dark:border-[oklch(0.46_0.02_264)] dark:bg-[oklch(0.38_0.022_264)] dark:text-gray-200"
        value={selectedTemplateName}
        onChange={handleTemplateChange}
        disabled={loading || templatePopulating || availableTemplates.length === 0}
      >
        {loading ? (
          <option value="" className="bg-white text-gray-700 dark:bg-[oklch(0.38_0.022_264)] dark:text-gray-200">Loading templates...</option>
        ) : availableTemplates.length === 0 ? (
          <option value="" className="bg-white text-gray-700 dark:bg-[oklch(0.38_0.022_264)] dark:text-gray-200">No templates available</option>
        ) : (
          availableTemplates.map((template) => (
            <option key={template._id} value={template.templateName} className="bg-white text-gray-700 dark:bg-[oklch(0.38_0.022_264)] dark:text-gray-200">
              {formatTemplateOptionLabel(template)}
            </option>
          ))
        )}
      </select>
    </div>
  );

  return (
    <>
      <TemplateSwitchConfirmModal
        open={templateSwitchModalOpen}
        onConfirm={confirmPendingTemplateSwitch}
        onCancel={cancelPendingTemplateSwitch}
      />
      {errorMsg && (
        <div className="mb-2 shrink-0 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
          {errorMsg}
        </div>
      )}
      <Accordion
        title={chargesOnly ? "Airline rates" : "Airline Conversation"}
        disabled={disabled}
        setOpen={setIsOpen}
        defaultOpen={Boolean(defaultOpen) && !disabled}
        highlight={selectedAirline?.emailThreadId === highlightThreadId}
        newMessageHeaderFlash={anyAirlinePulse}
        for="airline"
        openPanel={openPanel}
        onFullscreen={onFullscreen}
        isFullscreen={isFullscreen}
        showFullscreenButton={true}
        tallBody
        fillParentHeight={fillParentHeight}
        headerAction={
          !chargesOnly && (selectedAirline?.emailThreadId || threadId) ? (
            <div className="flex shrink-0 items-center gap-1.5">
              <button
                type="button"
                onClick={handleManualThreadRefresh}
                disabled={threadRefreshLoading || threadRefreshCooldown}
                title={
                  threadRefreshCooldown && !threadRefreshLoading
                    ? "Just refreshed"
                    : "Refresh thread"
                }
                aria-label="Refresh thread"
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200/90 bg-slate-50/90 text-slate-500 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-[oklch(0.4_0.02_264)] dark:bg-[oklch(0.225_0.026_264/0.8)] dark:text-slate-300 dark:hover:bg-[oklch(0.26_0.028_264)]"
              >
                <FontAwesomeIcon
                  icon={threadRefreshLoading ? faCircleNotch : faRotateRight}
                  className={`text-[12px] ${threadRefreshLoading ? "animate-spin" : ""}`}
                />
              </button>
            </div>
          ) : null
        }
      >
        <div className="flex h-full min-h-0 min-w-0 flex-col">
          <div
            ref={airlinePartnerSelectorRef}
            className="relative mb-2 w-full max-w-md shrink-0"
          >
            {finalAirlineId ? (
              <div className="block w-full rounded-[16px] border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-800 shadow-sm dark:border-[oklch(0.4_0.02_264)] dark:bg-[oklch(0.26_0.028_264)] dark:text-slate-100">
                {
                  (my_saved_airlines || []).filter((a) => a.airlineId == finalAirlineId)[0]
                    ?.airlineDetail?.name
                }
              </div>
            ) : (
              <>
                <button
                  type="button"
                  disabled={loading}
                  onClick={() => {
                    setAirlinePartnerMenuOpen((o) => !o);
                    if (airlinePartnerMenuOpen) setAirlinePartnerCreateOpen(false);
                  }}
                  className="flex w-full items-center justify-between gap-2 rounded-[16px] border border-slate-200 bg-white py-3 pl-4 pr-3 text-left text-sm font-medium text-slate-800 shadow-sm transition-colors focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-100 dark:border-[oklch(0.4_0.02_264)] dark:bg-[oklch(0.26_0.028_264)] dark:text-slate-100 dark:focus:ring-blue-900/40"
                >
                  <span className="truncate">
                    {selectedAirline?.airlineDetail?.name ||
                      airlineInput?.trim() ||
                      "Select your airline"}
                  </span>
                  <svg
                    className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${airlinePartnerMenuOpen ? "rotate-180" : ""}`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.23 7.21a.75.75 0 011.06.02L10 11.185l3.71-3.955a.75.75 0 111.08 1.04l-4.25 4.53a.75.75 0 01-1.08 0l-4.25-4.53a.75.75 0 01.02-1.06z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
                {airlinePartnerMenuOpen && (
                  <div
                    className={`absolute left-0 right-0 z-50 mt-1 overflow-y-auto rounded-2xl border border-slate-200 bg-white py-1 shadow-xl dark:border-[oklch(0.4_0.02_264)] dark:bg-[oklch(0.26_0.028_264)] ${airlinePartnerCreateOpen
                      ? "max-h-[min(36rem,55dvh)] [@media(max-height:720px)]:max-h-[min(24rem,48dvh)]"
                      : "max-h-[min(22rem,50dvh)] [@media(max-height:720px)]:max-h-[min(16rem,38dvh)]"
                      }`}
                  >
                    {airlinePartnerCreateOpen ? (
                      <>
                        <button
                          type="button"
                          className="w-full px-4 py-2.5 text-left text-sm font-medium text-fk-accent hover:bg-slate-50 dark:hover:bg-[oklch(0.3_0.024_264/0.8)]"
                          onClick={() => setAirlinePartnerCreateOpen(false)}
                        >
                          − Hide create form
                        </button>
                        <div className="space-y-3 border-t border-slate-200 bg-slate-50/95 p-3 dark:border-[oklch(0.4_0.02_264)] dark:bg-[oklch(0.225_0.026_264/0.6)]">
                          <div>
                            <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">
                              Airline name
                            </label>
                            <input
                              type="text"
                              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:border-[oklch(0.4_0.02_264)] dark:bg-[oklch(0.26_0.028_264)] dark:text-slate-100"
                              placeholder="Airline name"
                              value={airlineInput}
                              onChange={(e) => setAirlineInput(e.target.value)}
                            />
                          </div>
                          <div>
                            <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">
                              Airline email
                            </label>
                            <input
                              type="email"
                              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:border-[oklch(0.4_0.02_264)] dark:bg-[oklch(0.26_0.028_264)] dark:text-slate-100"
                              placeholder="Enter airline email..."
                              value={newAirline.email_address || ""}
                              onChange={(e) =>
                                setNewAirline((prev) => ({
                                  ...prev,
                                  email_address: e.target.value,
                                }))
                              }
                            />
                          </div>
                          <div>
                            <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">
                              Unique code
                            </label>
                            <input
                              type="text"
                              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:border-[oklch(0.4_0.02_264)] dark:bg-[oklch(0.26_0.028_264)] dark:text-slate-100"
                              placeholder="Enter unique code..."
                              value={newAirline.airlineCode || ""}
                              onChange={(e) => {
                                const code = e.target.value;
                                if (allAirlineCodes.includes(code)) {
                                  setErrorMsg(
                                    `Airline code must be unique. "${code}" is already in use.`
                                  );
                                  setNewAirline((prev) => ({ ...prev, airlineCode: "" }));
                                  return;
                                }
                                setNewAirline((prev) => ({ ...prev, airlineCode: code }));
                              }}
                            />
                          </div>
                          <button
                            type="button"
                            className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-3 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-wait disabled:bg-slate-400 disabled:hover:bg-slate-400"
                            disabled={createAirlineSubmitDisabled || airlinePartnerSaving}
                            onClick={handleCreateAndSaveAirlineToQuote}
                          >
                            {airlinePartnerSaving && (
                              <svg
                                className="h-4 w-4 shrink-0 animate-spin text-white"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                aria-hidden
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                />
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                />
                              </svg>
                            )}
                            {airlinePartnerSaving
                              ? "Saving…"
                              : airlines.some(
                                (a) =>
                                  (a.name || a._id).toLowerCase() ===
                                  airlineInput.trim().toLowerCase()
                              )
                                ? "Save airline to this quote"
                                : "Create & save new airline to this quote"}
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        {my_saved_airlines.length === 0 &&
                          directoryAirlinesForPartnerPicker.length === 0 && (
                            <p className="px-4 py-3 text-xs text-slate-500 dark:text-slate-400">
                              No airlines to choose from. Add airlines in Admin or use “Add / create
                              new airline” below.
                            </p>
                          )}
                        {my_saved_airlines.length > 0 && (
                          <p className="px-4 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                            On this quote
                          </p>
                        )}
                        {my_saved_airlines.map((airline) => (
                          <button
                            key={airline.airlineId}
                            type="button"
                            className="flex w-full items-center justify-between gap-2 px-4 py-2.5 text-left text-sm text-slate-800 hover:bg-slate-50 dark:text-slate-100 dark:hover:bg-[oklch(0.3_0.024_264/0.8)]"
                            onClick={() => {
                              setAirlineInput(airline.airlineDetail?.name || "");
                              setSelectedAirlineId(airline.airlineId);
                              setAirlinePulseMap((p) => {
                                const cp = { ...p };
                                delete cp[airline.airlineId];
                                return cp;
                              });
                              if (pulseTimersRef.current[airline.airlineId]) {
                                clearTimeout(pulseTimersRef.current[airline.airlineId]);
                                delete pulseTimersRef.current[airline.airlineId];
                              }
                              setAirlinePartnerMenuOpen(false);
                              setAirlinePartnerCreateOpen(false);
                            }}
                          >
                            <span className="min-w-0 flex-1">
                              <span className="block truncate font-medium">
                                {airline.airlineDetail?.name || airline.airlineId}
                              </span>
                              {(airline.airlineDetail?.email_address ||
                                airline.airlineDetail?.airlineCode) && (
                                  <span className="mt-0.5 block truncate text-xs text-slate-500 dark:text-slate-400">
                                    {[
                                      airline.airlineDetail?.email_address,
                                      airline.airlineDetail?.airlineCode,
                                    ]
                                      .filter(Boolean)
                                      .join(" · ")}
                                  </span>
                                )}
                            </span>
                            {airlinePulseMap[airline.airlineId] && (
                              <span
                                className="inline-flex h-2 w-2 shrink-0 animate-pulse rounded-full bg-blue-500"
                                aria-label="New message"
                                title="New message in this thread"
                              />
                            )}
                          </button>
                        ))}
                        {directoryAirlinesForPartnerPicker.length > 0 && (
                          <p className="px-4 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                            All airlines (directory)
                          </p>
                        )}
                        {directoryAirlinesForPartnerPicker.map((a) => (
                          <button
                            key={a._id}
                            type="button"
                            className="w-full px-4 py-2.5 text-left text-sm text-slate-800 hover:bg-slate-50 dark:text-slate-100 dark:hover:bg-[oklch(0.3_0.024_264/0.8)]"
                            onClick={() => {
                              setAirlineInput((a.name || "").trim());
                              setSelectedAirlineId("");
                              setNewAirline((prev) => ({
                                ...prev,
                                email_address: a.email_address || "",
                                airlineCode: a.airlineCode || "",
                              }));
                              setAirlinePartnerCreateOpen(true);
                            }}
                          >
                            <span className="block font-medium">{a.name || a._id}</span>
                            {(a.email_address || a.airlineCode) && (
                              <span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-400">
                                {[a.email_address, a.airlineCode].filter(Boolean).join(" · ")}
                              </span>
                            )}
                          </button>
                        ))}
                        <div className="border-t border-slate-200 dark:border-[oklch(0.4_0.02_264)]">
                          <button
                            type="button"
                            className="w-full px-4 py-2.5 text-left text-sm font-medium text-fk-accent hover:bg-slate-50 dark:hover:bg-[oklch(0.3_0.024_264/0.8)]"
                            onClick={() => setAirlinePartnerCreateOpen(true)}
                          >
                            + Add / create new airline
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {!chargesOnly && (
            <div data-tour="airline-section-tabs" className="mb-2 flex shrink-0 gap-1 rounded-xl border border-slate-200/80 bg-slate-50/90 p-1 dark:border-[oklch(0.33_0.022_264/0.6)] dark:bg-[oklch(0.225_0.026_264/0.5)]">
              <button
                type="button"
                onClick={() => setSectionTab("charges")}
                className={`flex-1 rounded-lg px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wide transition-all sm:text-[12px] sm:normal-case sm:tracking-normal ${sectionTab === "charges"
                  ? "bg-blue-500/10 text-fk-accent shadow-sm ring-1 ring-blue-200/70 dark:bg-blue-500/15 dark:text-blue-300 dark:ring-blue-500/30"
                  : "text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
                  }`}
              >
                Charges &amp; details
              </button>
              <button
                type="button"
                onClick={() => setSectionTab("messages")}
                className={`flex-1 rounded-lg px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wide transition-all sm:text-[12px] sm:normal-case sm:tracking-normal ${sectionTab === "messages"
                  ? "bg-blue-500/10 text-fk-accent shadow-sm ring-1 ring-blue-200/70 dark:bg-blue-500/15 dark:text-blue-300 dark:ring-blue-500/30"
                  : "text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
                  }`}
              >
                Messages
              </button>
            </div>
          )}

          {(chargesOnly || sectionTab === "charges") && (
            <div data-tour="airline-rates" className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto pr-0.5">
              {/* LEGACY: typeahead + duplicate “Selected Airline” card — superseded by unified selector above tabs; set to true to restore */}
              {false && (
                <div className="mb-4">
                  {!finalAirlineId && <div className="mb-4 flex-row">
                    <div className="flex items-center gap-x-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1 transition-colors duration-300">
                        Select Airline
                      </label>
                      <div className="relative w-64">
                        <input
                          type="text"
                          className="w-full p-2 border rounded-md bg-white dark:bg-[oklch(0.38_0.022_264)] text-gray-800 dark:text-gray-200 border-gray-300 dark:border-[oklch(0.46_0.02_264)] outline-none transition-colors duration-300"
                          placeholder="Type to add or select airline..."
                          value={airlineInput}
                          onChange={(e) => {
                            setAirlineInput(e.target.value);
                            setShowAirlineSuggestions(true);
                          }}
                          onFocus={() => setShowAirlineSuggestions(true)}
                          onBlur={() => setTimeout(() => setShowAirlineSuggestions(false), 200)}
                        />
                        {showAirlineSuggestions && (filteredAirlines.length > 0 || my_saved_airlines_filtered.length > 0) && (
                          <div className="absolute top-full left-0 mt-1 w-full bg-white dark:bg-[oklch(0.38_0.022_264)] border-gray-300 dark:border-[oklch(0.46_0.02_264)] border rounded shadow z-50 max-h-60 overflow-y-auto transition-colors duration-300">
                            <div className="border-b-2 border-gray-200 dark:border-[oklch(0.46_0.02_264)] px-4 py-2 font-semibold">
                              {my_saved_airlines_filtered.map((airline) => (
                                <div
                                  key={airline.airlineId}
                                  className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-[oklch(0.45_0.02_264)] text-gray-800 dark:text-gray-200 cursor-pointer transition-colors duration-300"
                                  onClick={() => {
                                    setAirlineInput(airline.airlineDetail?.name);
                                    setSelectedAirlineId(airline.airlineId)
                                    setShowAirlineSuggestions(false);
                                  }}
                                >
                                  {airline.airlineDetail?.name || airline.airlineId}
                                </div>
                              ))}
                            </div>
                            <div>
                              {filteredAirlines.map((airline) => (
                                <div
                                  key={airline.airlineId}
                                  className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-[oklch(0.45_0.02_264)] text-gray-800 dark:text-gray-200 cursor-pointer transition-colors duration-300"
                                  onClick={() => {
                                    setAirlineInput(airline.name);
                                    setSelectedAirlineId('')
                                    setShowAirlineSuggestions(false);
                                  }}
                                >
                                  {airline.airlineDetail?.name || airline.airlineId} {airline.name}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    {airlineInput.trim() && (my_saved_airlines_filtered.length == 0 && filteredAirlines.length == 0) && (<>
                      <div className="flex gap-x-2">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1 transition-colors duration-300">
                            Airline Email
                          </label>
                          <div className="relative w-64">
                            <input
                              type="email"
                              className="w-full p-2 border rounded-md bg-white dark:bg-[oklch(0.38_0.022_264)] text-gray-800 dark:text-gray-200 border-gray-300 dark:border-[oklch(0.46_0.02_264)] outline-none transition-colors duration-300"
                              placeholder="Enter airline email..."
                              value={newAirline.email_address || ""}
                              onChange={(e) => {
                                const email = e.target.value;
                                setNewAirline(prev => ({ ...prev, email_address: email }));
                              }}
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1 transition-colors duration-300">
                            Airline Unique Code
                          </label>
                          <div className="relative w-64">
                            <input
                              type="text"
                              className="w-full p-2 border rounded-md bg-white dark:bg-[oklch(0.38_0.022_264)] text-gray-800 dark:text-gray-200 border-gray-300 dark:border-[oklch(0.46_0.02_264)] outline-none transition-colors duration-300"
                              placeholder="Enter unique code..."
                              value={newAirline.airlineCode || ""}
                              onChange={(e) => {
                                const code = e.target.value;
                                if (allAirlineCodes.includes(code)) {
                                  setErrorMsg(`Airline code must be unique. "${code}" is already in use.`);
                                  setNewAirline(prev => ({ ...prev, airlineCode: "" }));
                                  return;
                                }
                                setNewAirline(prev => ({ ...prev, airlineCode: code }));
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </>)}
                    <div className="flex items-center justify-start my-2 gap-x-2">
                      <button
                        className="px-3 py-1 rounded text-white disabled:bg-gray-400 bg-blue-600 hover:bg-blue-700"
                        onClick={async () => {
                          const airlineName = airlineInput.trim().toLowerCase();
                          const matchedAirline = airlines.find(
                            a => (a.name || a._id).toLowerCase() === airlineName
                          );
                          let TheAirlineToSave = null;
                          if (!matchedAirline) {
                            const airlineToSave = {
                              ...newAirline,
                              name: airlineInput.trim(),
                            };
                            const myNewAirline = await createNewAirline(airlineToSave);
                            if (myNewAirline) {
                              TheAirlineToSave = myNewAirline;
                              setAirlines(prev => [...prev, myNewAirline]);
                            }
                          } else {
                            TheAirlineToSave = matchedAirline;
                          }
                          if (TheAirlineToSave) {
                            await saveMyNewAirlineWithQuote(TheAirlineToSave);
                            setNewAirline({ name: "", airlineCode: "", email_address: "", description: "" });
                            setAirlineInput("");
                            setRefetchTrigger((prev) => prev + 1);
                            setSelectedAirlineId(TheAirlineToSave._id)
                          } else {
                            showSwal("Failure", "Failed to create airline. Please try again.", 400);
                          }
                        }}
                        disabled={
                          !airlineInput.trim() ||
                          my_saved_airlines.some(
                            a =>
                              (a.airlineDetail?.name || a.airlineId).toLowerCase() ===
                              airlineInput.trim().toLowerCase()
                          )
                        }
                      >
                        {
                          airlines.some(
                            a =>
                              (a.name || a._id).toLowerCase() ===
                              airlineInput.trim().toLowerCase()
                          )
                            ? "Save Airline to this Quote"
                            : "Create & Save new Airline to this Quote"
                        }
                      </button>
                    </div>
                  </div>}
                  <div className="rounded-[24px] border border-slate-200/90 bg-slate-50/80 p-4 shadow-[0_12px_30px_rgba(148,163,184,0.10)]">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                      Selected Airline
                    </p>
                    <p className="mt-2 text-sm text-slate-500">
                      Pick the airline whose negotiated rates should be reviewed and updated below.
                    </p>
                    <div className="relative mt-4 w-full max-w-sm">
                      {!finalAirlineId ? <>
                        <select
                          value={selectedAirlineId}
                          disabled={loading}
                          onChange={(e) => setSelectedAirlineId(e.target.value)}
                          className="block w-full appearance-none rounded-[16px] border border-slate-200 bg-white py-3 pl-4 pr-10 text-sm font-medium text-slate-800 shadow-sm transition-colors duration-300 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-100"
                        >
                          <option key={null} value={""} className="bg-white text-slate-800">
                            Select Your Airline
                          </option>
                          {my_saved_airlines.map((airline) => (
                            <option key={airline.airlineId} value={airline.airlineId} className="bg-white text-slate-800">
                              {airline.airlineDetail?.name || airline.airlineId}
                            </option>
                          ))}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-slate-400">
                          <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path
                              fillRule="evenodd"
                              d="M5.23 7.21a.75.75 0 011.06.02L10 11.185l3.71-3.955a.75.75 0 111.08 1.04l-4.25 4.53a.75.75 0 01-1.08 0l-4.25-4.53a.75.75 0 01.02-1.06z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                      </> : <div className="block w-full rounded-[16px] border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-800 shadow-sm">
                        {(my_saved_airlines || []).filter(a => a.airlineId == finalAirlineId)[0]?.airlineDetail?.name}
                      </div>}
                    </div>
                  </div>
                </div>
              )}
              <FinalRateSection
                openRates={openPanel?.airline?.openRates}
                selectedAgentId={selectedAirlineId}
                isEditing={isEditing}
                setIsEditing={setIsEditing}
                finalRates={finalRates}
                setFinalRates={setFinalRates}
                // Fall back to the URL :id when no airline is selected yet (or the
                // saved airline row doesn't carry quoteId) — without this fallback,
                // FinalRateSection's preflight rejected every save with "Quote ID
                // is missing" on a fresh manual query because selectedAirline was
                // null until the dropdown got picked.
                quoteId={selectedAirline?.quoteId || quoteId} token={loginToken}
                setRefetchTrigger={setRefetchTrigger}
                setFqctTrigger={setFqctTrigger}
                isAirline={true}
                role={userLoggedIn?.role}
                updateApi={updateSavedAirlineRate}
                hasSavedRates={recordHasRates(selectedAirline)}
              />
            </div>
          )}

          {!chargesOnly && sectionTab === "messages" && (
            <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
              {loading && (
                <div className="mb-3 flex shrink-0 items-center gap-2 rounded-md border border-violet-200/60 bg-violet-50/60 px-3 py-2 text-[12px] font-medium text-violet-700 dark:border-violet-500/30 dark:bg-violet-950/30 dark:text-violet-300">
                  <FontAwesomeIcon icon={faCircleNotch} className="animate-spin text-[11px]" />
                  Loading airline conversation…
                </div>
              )}
              {loading && combinedAirlineMessages.length === 0 ? (
                <EmailThreadSkeleton messages={5} />
              ) : (
                <>
                  <div className="mb-2 shrink-0 text-xs text-gray-500 transition-colors duration-300 dark:text-gray-400">
                    {combinedAirlineMessages.length} message{combinedAirlineMessages.length !== 1 && "s"}{" "}
                    {lastUpdated && `· Last updated ${lastUpdated.toLocaleTimeString()}`}
                  </div>

                  <EmailThread
                    toEmail={resolvedAirlineToEmail || undefined}
                    recipientSyncKey={selectedAirlineId || undefined}
                    messages={combinedAirlineMessages}
                    threadId={selectedAirline?.emailThreadId || threadId}
                    onReplySubmit={(selectedAirline?.emailThreadId || threadId) ? handleReplySubmit : handleInitialEmailSend}
                    isInitialSubmitType={airlineInput == "" ? selectedAirlineId : airlineInput == selectedAirline?.airlineDetail?.name}
                    templateData={templateData}
                    USER_EMAIL={USER_EMAIL}
                    order="asc"
                    bottomRef={bottomRef}
                    variant="flat"
                    roomyComposer
                    naturalStackHeight={false}
                    composerAddon={airlineTemplateComposerAddon}
                    conversationScrollKey={
                      selectedAirline?.emailThreadId || threadId || selectedAirlineId || ""
                    }
                    onReplyDraftDirtyChange={onReplyDraftDirty}
                    collapsibleComposer
                    composerTourId="qd-reply-composer"
                    onMessageAssist={loginToken ? handleMessageAssist : undefined}
                    messageAssistInFlight={assistLoading}
                  />
                </>
              )}
            </div>
          )}
        </div>
      </Accordion>
      <AiMessageAssistPopover
        open={assistOpenForMessageId != null}
        anchorRect={assistAnchorRect}
        loading={assistLoading}
        error={assistError}
        data={assistData}
        onClose={closeAssist}
        onRegenerate={handleAssistRegenerate}
        onCopy={handleAssistCopy}
        kind="airline"
        onExtract={handleExtract}
        extractLoading={extractLoading}
        extractError={extractError}
        extractedData={extractedData}
        extractedWanted={extractedWanted}
        onApply={handleApply}
        applying={applying}
      />
    </>
  );
}
